import { POOLS_MAP, PoolName, CRO, WCRO } from "../../constants"
import React, { ReactElement, useEffect, useState, useMemo } from "react"
import WithdrawPage, { ReviewWithdrawData } from "../../components/WithdrawPage"
import { commify, formatUnits, parseUnits } from "@ethersproject/units"
import { AppState } from "../../state"
import { BigNumber } from "@ethersproject/bignumber"
import { Zero } from "@ethersproject/constants"
import { calculateGasEstimate } from "../../utils/gasEstimate"
import { calculatePriceImpact } from "../../utils/priceImpact"
import { formatGasToString } from "../../utils/gas"
import { formatSlippageToString, subtractSlippage } from "../../utils/slippage"
import { useApproveAndWithdraw } from "../../hooks/useApproveAndWithdraw"
import { useSelector } from "react-redux"
import useWithdrawFormState from "../../hooks/useWithdrawFormState"
import { PoolDataType, UserShareType } from "src/hooks/usePoolData"
import { TransactionSummaryData } from "src/components/TransactionSummary"
import { toSignificant } from "src/utils"
import { getSwapContract } from "src/utils/contractHelpers"

interface Props {
  poolName: PoolName
  userShareData: UserShareType | undefined
  poolData: PoolDataType
}
function Withdraw({ poolName, poolData, userShareData }: Props): ReactElement {
  const POOL = POOLS_MAP[poolName]
  const poolTokens = useMemo(() => {
    const tokens = POOL.poolTokens.slice()
    const croIdx = tokens.indexOf(WCRO)
    if (croIdx > -1) {
      tokens.splice(croIdx, 1, CRO)
    }
    return tokens
  }, [POOL])

  const [withdrawFormState, updateWithdrawFormState] = useWithdrawFormState(
    poolName,
    poolTokens,
  )
  const { slippageCustom, slippageSelected, gasPriceSelected, gasCustom } =
    useSelector((state: AppState) => state.user)
  const { tokenPricesUSD, gasStandard, gasFast, gasInstant } = useSelector(
    (state: AppState) => state.application,
  )
  const approveAndWithdraw = useApproveAndWithdraw(poolName)
  const swapContract = getSwapContract(poolName)
  const [estWithdrawBonus, setEstWithdrawBonus] = useState(Zero)
  useEffect(() => {
    // evaluate if a new withdraw will exceed the pool's per-user limit
    function calculateWithdrawBonus() {
      if (swapContract == null) {
        return
      }
      const tokenInputSum = parseUnits(
        poolTokens
          .reduce(
            (sum, { symbol }) =>
              sum + (+withdrawFormState.tokenInputs[symbol].valueRaw || 0),
            0,
          )
          .toFixed(18),
        18,
      )
      let withdrawLPTokenAmount
      if (poolData.totalLocked.gt(0) && tokenInputSum.gt(0)) {
        withdrawLPTokenAmount = withdrawFormState.lpTokenAmountToSpend
      } else {
        // when pool is empty, estimate the lptokens by just summing the input instead of calling contract
        withdrawLPTokenAmount = tokenInputSum
      }
      setEstWithdrawBonus(
        calculatePriceImpact(
          withdrawLPTokenAmount,
          tokenInputSum,
          poolData.virtualPrice,
          true,
        ),
      )
    }
    void calculateWithdrawBonus()
  }, [poolData, withdrawFormState, swapContract, poolTokens])
  async function onConfirmTransaction(
    summary: TransactionSummaryData,
  ): Promise<void> {
    const { withdrawType, tokenInputs, lpTokenAmountToSpend } =
      withdrawFormState
    await approveAndWithdraw(
      {
        tokenFormState: tokenInputs,
        withdrawType,
        lpTokenAmountToSpend,
      },
      summary,
    )
    updateWithdrawFormState({ fieldName: "reset", value: "reset" })
  }

  const tokensData = React.useMemo(
    () =>
      poolTokens.map(({ name, symbol, icon, decimals }) => ({
        name,
        symbol,
        icon,
        inputValue: withdrawFormState.tokenInputs[symbol].valueRaw,
        decimals,
      })),
    [withdrawFormState, poolTokens],
  )
  const gasPrice = BigNumber.from(
    formatGasToString(
      { gasStandard, gasFast, gasInstant },
      gasPriceSelected,
      gasCustom,
    ),
  )
  const gasAmount = calculateGasEstimate("removeLiquidityImbalance").mul(
    gasPrice,
  ) // units of gas * GWEI/Unit of gas

  const txnGasCost = {
    amount: gasAmount,
    valueUSD: tokenPricesUSD?.ETH
      ? parseUnits(tokenPricesUSD.ETH.toFixed(2), 18) // USD / ETH  * 10^18
          .mul(gasAmount) // GWEI
          .div(BigNumber.from(10).pow(25)) // USD / ETH * GWEI * ETH / GWEI = USD
      : null,
  }

  const reviewWithdrawData: ReviewWithdrawData = {
    withdraw: [],
    rates: [],
    slippage: formatSlippageToString(slippageSelected, slippageCustom),
    priceImpact: estWithdrawBonus,
    txnGasCost: txnGasCost,
  }
  poolTokens.forEach(({ name, decimals, icon, symbol, addresses }) => {
    // if (BigNumber.from(withdrawFormState.tokenInputs[symbol].valueSafe).gt(0)) {
    reviewWithdrawData.withdraw.push({
      symbol,
      name,
      addresses,
      value: commify(
        toSignificant(
          formatUnits(
            subtractSlippage(
              BigNumber.from(withdrawFormState.tokenInputs[symbol].valueSafe),
              slippageSelected,
              slippageCustom,
            ),
            decimals,
          ),
          6,
        ),
      ),
      icon,
      decimals,
    })
    if (tokenPricesUSD != null) {
      reviewWithdrawData.rates.push({
        name,
        value: formatUnits(
          withdrawFormState.tokenInputs[symbol].valueSafe,
          decimals,
        ),
        rate: commify(tokenPricesUSD[symbol]?.toFixed(2) || ""),
      })
    }
    // }
  })

  return (
    <WithdrawPage
      title={poolName}
      reviewData={reviewWithdrawData}
      tokensData={tokensData}
      poolData={poolData}
      myShareData={userShareData}
      formStateData={withdrawFormState}
      onConfirmTransaction={onConfirmTransaction}
      onFormChange={updateWithdrawFormState}
    />
  )
}

export default Withdraw
